import React, { Component } from 'react';
import { matchPath } from 'react-router';
import { Link } from 'react-router-dom';
import StarRatingComponent from 'react-star-rating-component';
import Helmet from 'react-helmet';
import DOMPurify from 'dompurify';
import mixpanel, { initMixpanel } from '../utils/mixpanelConsent';

import AuthService from './AuthService';
import AuthModalContext from '../contexts/AuthModalContext';
import { renderRarityBadge } from '../utils/rarityBadge';
import CartContext from '../contexts/CartContext';
import constants from '../config';
import { resolveCardImage } from '../utils/resolveImage';
import { saveShareRef, loadShareRef, clearShareRef } from '../utils/shareRef';
import { formatSealedType } from '../utils/sealedTypes';

const Auth = new AuthService();
initMixpanel();

var TRACKING_UPGRADE_FEE = constants.TRACKING_UPGRADE_FEE || 5.50;
var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
var TRACKING_REQUIRED_THRESHOLD = constants.TRACKING_REQUIRED_THRESHOLD || 50;
var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;
var SHARE_MIN_PRICE = constants.SHARE_MIN_PRICE || 10;

// renderRarityBadge imported from utils/rarityBadge

class ProductViewRedesign extends Component {
  static contextType = AuthModalContext;

  constructor(props) {
    super(props);
    this.state = {
      productId: '',
      title: '',
      images: [],
      selectedImageIndex: 0,
      description: '',
      price: 0,
      category: '',
      shippingCost: 0,
      sold: false,
      quantity: 1,
      selectedQuantity: 1,
      seller: {},
      isFollowingSeller: false,
      loading: true,
      isOwner: false,
      stripeCustomerId: null,
      savedCard: null,
      buyError: '',
      purchaseClick: 0,
      sellerPosts: [],
      reportModalOpen: false,
      reportReason: '',
      reportDetails: '',
      reportSubmitting: false,
      reportSuccess: false,
      reportAlreadyReported: false,
      hasReported: false,
      cardRarity: null,
      cardHorizontal: false,
      cardRotation: 0,
      linkedCardId: null,
      catalogImage: null,
      conditionPhotos: [],
      acceptOffers: false,
      offerModalOpen: false,
      offerAmount: '',
      offerSending: false,
      offerSent: false,
      offerError: '',
      showShareModal: false,
      shareLink: '',
      shareLoading: false,
      shareCopied: false,
      alreadyShared: false,
      allowSharing: true,
      shareCommission: 1,
      shareCommissionType: 'amount',
      shareCount: 0,
      shareRef: null,
      sharerPayoutChecked: false,
      sellerShopActive: true,
      trackingUpgrade: false,
      taxRate: 0,
      messageModalOpen: false,
      messageText: '',
      messageSending: false,
      messageSent: false,
      notFound: false,
    };
    this.delete = this.delete.bind(this);
    this.report = this.report.bind(this);
    this.undoReport = this.undoReport.bind(this);
    this.toggleFollowSeller = this.toggleFollowSeller.bind(this);
    this.toPurchase = this.toPurchase.bind(this);
    this.toCheckout = this.toCheckout.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.openShareModal = this.openShareModal.bind(this);
    this.copyShareLink = this.copyShareLink.bind(this);
    this.handleMessageSeller = this.handleMessageSeller.bind(this);
    this.handleSendMessage = this.handleSendMessage.bind(this);
    this.handleMakeOffer = this.handleMakeOffer.bind(this);
    this.handleSendOffer = this.handleSendOffer.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const match = matchPath(this.props.history.location.pathname, {
      path: '/product/:product_id',
      exact: false,
      strict: false,
    });
    if (match) {
      var productId = match.params.product_id;
      // Check for share referral param (?ref=shareId)
      var search = this.props.location && this.props.location.search;
      var params = new URLSearchParams(search || '');
      var ref = params.get('ref');
      if (ref) {
        this.setState({ shareRef: ref });
        saveShareRef(productId, ref);
      } else {
        var savedRef = loadShareRef(productId);
        if (savedRef) {
          this.setState({ shareRef: savedRef });
        }
      }
      this.fetchProduct(productId);
      mixpanel.track('ProductPicked');
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      const currMatch = matchPath(this.props.location.pathname, {
        path: '/product/:product_id',
        exact: false,
        strict: false,
      });
      if (currMatch) {
        var cdSearch = this.props.location && this.props.location.search;
        var cdParams = new URLSearchParams(cdSearch || '');
        var cdRef = cdParams.get('ref');
        if (cdRef) {
          this.setState({ shareRef: cdRef });
          saveShareRef(currMatch.params.product_id, cdRef);
        } else {
          var cdSavedRef = loadShareRef(currMatch.params.product_id);
          if (cdSavedRef) {
            this.setState({ shareRef: cdSavedRef });
          }
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        this.setState({ selectedImageIndex: 0, linkedCardId: null, cardRarity: null });
        this.fetchProduct(currMatch.params.product_id);
        mixpanel.track('ProductPicked');
      }
    }
  }

  fetchProduct(productId) {
    const isLoggedIn = Auth.loggedIn();
    const endpoint = isLoggedIn
      ? `${constants.DOMAIN}/api/v1/posts/${productId}`
      : `${constants.DOMAIN}/api/v1/guest/post/${productId}`;

    const fetchFn = isLoggedIn
      ? Auth.fetch(endpoint, { mode: 'cors', method: 'GET' })
      : fetch(endpoint, { mode: 'cors', method: 'GET' }).then(r => r.json());

    fetchFn
      .then(res => {
        // Handle missing/deleted listings
        if (!res || !res.title || !res.user) {
          this.setState({ loading: false, notFound: true });
          return;
        }
        const userImages = (res.images || []).filter(img => img);
        let isOwner = false;
        if (isLoggedIn) {
          try {
            const user = Auth.getProfile();
            isOwner = String(user._id) === String(res.user._id);
          } catch (e) { /* ignore */ }
        }

        // Catalog image = main display image; condition photos = thumbnail strip
        var catalogImg = null;

        if (res.cardImage) {
          catalogImg = resolveCardImage(res.cardImage);
        }

        // Main image: catalog image if available, otherwise first user photo
        var images = catalogImg ? [catalogImg] : userImages.slice(0, 1);
        if (images.length === 0 && userImages.length > 0) images = [userImages[0]];

        this.setState({
          productId,
          title: res.title,
          images,
          conditionPhotos: userImages,
          catalogImage: catalogImg,
          description: res.text,
          price: res.price,
          category: res.category || '',
          shippingCost: res.shippingCost || 0,
          sold: res.sold,
          quantity: res.quantity || 1,
          selectedQuantity: 1,
          seller: res.user || {},
          isOwner,
          loading: false,
          sellerShopActive: res.user ? res.user.shopActive !== false : true,
          allowSharing: res.allowSharing !== false,
          shareCommission: res.shareCommission || 5,
          shareCommissionType: res.shareCommissionType || 'percentage',
          acceptOffers: res.acceptOffers === true,
          shareCount: res.shareCount || 0,
          sharedBy: res.sharedBy || [],
          hasReported: !!res.reported,
          foil: res.foil || false,
          condition: res.condition || (res.category ? res.category.replace(/^singles\s*/i, '').trim() : null),
          productType: res.productType || 'single',
          sealedType: res.sealedType || null,
        });

        // Fetch card rarity if post is linked to a card
        if (res.cards && res.cards.length > 0) {
          var firstCard = res.cards[0];
          var cardId = typeof firstCard === 'object' && firstCard._id ? firstCard._id : firstCard;
          this.setState({ linkedCardId: cardId });

          // Use populated card data if available, otherwise fetch
          if (typeof firstCard === 'object' && firstCard._id) {
            if (firstCard.rarity) this.setState({ cardRarity: firstCard.rarity });
            this.setState({
              cardHorizontal: firstCard.horizontal === true,
              cardRotation: firstCard.rotation || -90,
            });
            if (!res.cardImage && firstCard.image) {
              var popCatImg = resolveCardImage(firstCard.image);
              this.setState({ catalogImage: popCatImg, images: [popCatImg], selectedImageIndex: 0 });
            }
          }

          fetch(`${constants.DOMAIN}/api/v1/guest/card/${cardId}`, { mode: 'cors', method: 'GET' })
            .then(r => r.json())
            .then(cardData => {
              if (cardData && cardData.rarity) {
                this.setState({ cardRarity: cardData.rarity });
              }
              if (cardData) {
                this.setState({
                  cardHorizontal: cardData.horizontal === true,
                  cardRotation: cardData.rotation || -90,
                });
              }
              // If no cardImage on post, use card's catalog image as main
              if (!res.cardImage && cardData && cardData.image) {
                var catImg = resolveCardImage(cardData.image);
                this.setState({
                  catalogImage: catImg,
                  images: [catImg],
                  selectedImageIndex: 0,
                });
              }
            })
            .catch(() => {});
        }

        // Fetch stripe customer ID for 1-click buy
        if (isLoggedIn) {
          try {
            const user = Auth.getProfile();
            Auth.fetch(`${constants.DOMAIN}/api/v1/users/${user._id}`, {
              mode: 'cors',
              method: 'GET',
            }).then(profileRes => {
              const followingList = profileRes.profile.isFollowing || [];
              const sellerId = res.user._id;
              this.setState({
                stripeCustomerId: profileRes.profile.stripeCustomerId || null,
                isFollowingSeller: followingList.some(id => String(id) === String(sellerId)),
              });
              // Fetch saved card details for 1-click buy label
              if (profileRes.profile.stripeCustomerId) {
                Auth.fetch(`${constants.DOMAIN}/api/v1/transactions/saved-card`, {
                  mode: 'cors',
                  method: 'GET',
                }).then(cardRes => {
                  if (cardRes.card) this.setState({ savedCard: cardRes.card });
                }).catch(() => {});
              }
              // Fetch tax rate from saved address for 1-click buy total
              var addr = profileRes.profile.address;
              if (addr && addr.zip && addr.state && addr.zip.trim().length === 5) {
                fetch(constants.DOMAIN + '/api/v1/guest/tax-rate?zip=' + encodeURIComponent(addr.zip.trim()) + '&state=' + encodeURIComponent(addr.state.trim()), {
                  mode: 'cors',
                  method: 'GET',
                })
                  .then(r => r.json())
                  .then(taxData => {
                    if (taxData.rate) this.setState({ taxRate: taxData.rate });
                  })
                  .catch(() => {});
              }
            }).catch(() => {});
          } catch (e) { /* ignore */ }
        }

        // Fetch seller's other listings
        if (res.user && res.user.userName) {
          fetch(`${constants.DOMAIN}/api/v1/guest/meta/${res.user.userName}`, {
            mode: 'cors',
            method: 'GET',
          })
            .then(r => r.json())
            .then(sellerRes => {
              const otherPosts = (sellerRes.sortedProfile || [])
                .filter(p => String(p._id) !== String(productId) && !p.sold)
                .slice(0, 8);
              this.setState({ sellerPosts: otherPosts });
            })
            .catch(() => {});
        }

      })
      .catch(err => {
        console.log('Error fetching product:', err);
        this.setState({ loading: false, notFound: true });
      });
  }


  delete() {
    Auth.fetch(`${constants.DOMAIN}/api/v1/posts/${this.state.productId}`, {
      mode: 'cors',
      method: 'DELETE',
    })
      .then(() => {
        alert('Product deleted!');
        this.props.history.push('/');
      })
      .catch(err => console.log(err));
  }

  report() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login', () => this.report());
      return;
    }
    this.setState({ reportModalOpen: true, reportReason: '', reportDetails: '', reportSuccess: false, reportAlreadyReported: false });
  }

  submitReport() {
    const { productId, reportReason, reportDetails } = this.state;
    if (!reportReason) return;
    this.setState({ reportSubmitting: true });
    Auth.fetch(`${constants.DOMAIN}/api/v1/posts/${productId}/report`, {
      mode: 'cors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reportReason, details: reportDetails }),
    })
      .then(res => {
        if (res.alreadyReported) {
          this.setState({ reportSubmitting: false, reportAlreadyReported: true, hasReported: true });
        } else {
          this.setState({ reportSubmitting: false, reportSuccess: true, hasReported: true });
        }
      })
      .catch(err => {
        console.log(err);
        this.setState({ reportSubmitting: false });
      });
  }

  undoReport() {
    const { productId } = this.state;
    this.setState({ reportSubmitting: true });
    Auth.fetch(`${constants.DOMAIN}/api/v1/posts/${productId}/report`, {
      mode: 'cors',
      method: 'DELETE',
    })
      .then(() => {
        this.setState({ reportSubmitting: false, reportModalOpen: false, reportAlreadyReported: false, hasReported: false, reportSuccess: false });
      })
      .catch(err => {
        console.log(err);
        this.setState({ reportSubmitting: false });
      });
  }

  toggleFollowSeller() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login', () => this.toggleFollowSeller());
      return;
    }
    const sellerId = this.state.seller._id;
    if (!sellerId) return;
    const isFollowing = this.state.isFollowingSeller;
    const method = isFollowing ? 'DELETE' : 'POST';
    const endpoint = isFollowing
      ? `${constants.DOMAIN}/api/v1/users/${sellerId}/unfollow`
      : `${constants.DOMAIN}/api/v1/users/${sellerId}/follow`;
    Auth.fetch(endpoint, { mode: 'cors', method })
      .then(() => {
        this.setState({ isFollowingSeller: !isFollowing });
      })
      .catch(err => console.log(err));
  }

  handleMessageSeller() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login', () => this.handleMessageSeller());
      return;
    }
    this.setState({ messageModalOpen: true, messageText: '', messageSending: false, messageSent: false });
  }

  handleSendMessage() {
    var self = this;
    var text = self.state.messageText.trim();
    if (!text) return;
    var sellerId = self.state.seller._id;
    if (!sellerId) return;
    self.setState({ messageSending: true });
    Auth.fetch(constants.DOMAIN + '/api/v1/messages', {
      mode: 'cors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: sellerId }),
    })
      .then(function(data) {
        if (!data.conversation) throw new Error('No conversation returned');
        return Auth.fetch(constants.DOMAIN + '/api/v1/messages/' + data.conversation._id, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text, attachedPostId: self.state.productId }),
        });
      })
      .then(function() {
        self.setState({ messageSending: false, messageSent: true });
      })
      .catch(function(err) {
        console.log('Send message error:', err);
        self.setState({ messageSending: false });
        alert('Failed to send message. Please try again.');
      });
  }

  handleMakeOffer() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login', () => this.handleMakeOffer());
      return;
    }
    this.setState({ offerModalOpen: true, offerAmount: '', offerSending: false, offerSent: false, offerError: '' });
  }

  handleSendOffer() {
    var self = this;
    var amount = Number(self.state.offerAmount);
    var minOffer = 0.75;
    if (self.state.allowSharing && self.state.shareCommission) {
      var shareMin = Math.ceil((Math.max(1, Number(self.state.shareCommission)) + 1.00) / 0.90 * 100) / 100;
      if (shareMin > minOffer) minOffer = shareMin;
    }
    // 20% of listing price floor
    var priceFloor = Math.ceil(Number(self.state.price) * 0.20 * 100) / 100;
    if (priceFloor > minOffer) minOffer = priceFloor;
    if (!amount || amount < minOffer) {
      self.setState({ offerError: 'Minimum offer is $' + minOffer.toFixed(2) });
      return;
    }
    if (amount >= self.state.price) {
      self.setState({ offerError: 'Offer must be less than the listed price' });
      return;
    }
    self.setState({ offerSending: true, offerError: '' });
    var sellerId = self.state.seller._id;
    // First get or create conversation
    Auth.fetch(constants.DOMAIN + '/api/v1/messages', {
      mode: 'cors',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: sellerId }),
    })
      .then(function(data) {
        if (!data.conversation) throw new Error('No conversation returned');
        // Then send the offer
        return Auth.fetch(constants.DOMAIN + '/api/v1/messages/' + data.conversation._id + '/offer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ amount: amount, postId: self.state.productId }),
        });
      })
      .then(function() {
        self.setState({ offerSending: false, offerSent: true });
      })
      .catch(function(err) {
        console.log('Send offer error:', err);
        if (err.response) {
          err.response.json().then(function(data) {
            self.setState({ offerSending: false, offerError: data.error || 'Failed to send offer. Please try again.' });
          }).catch(function() {
            self.setState({ offerSending: false, offerError: 'Failed to send offer. Please try again.' });
          });
        } else {
          self.setState({ offerSending: false, offerError: 'Failed to send offer. Please try again.' });
        }
      });
  }

  toPurchase() {
    if (this.state.sold) {
      this.setState({ buyError: 'Item was already sold.' });
      return;
    }
    var self = this;
    this.setState(
      prev => ({ purchaseClick: prev.purchaseClick + 1 }),
      () => {
        if (self.state.purchaseClick === 1) {
          var purchaseId = self.state.shareRef || self.state.productId;
          var endpoint = self.state.shareRef ? 'customermetabuy' : 'customerbuy';
          var trackingParam = self.state.trackingUpgrade ? '?trackingUpgrade=true' : '';
          Auth.fetch(constants.DOMAIN + '/api/v1/transactions/' + purchaseId + '/' + endpoint + trackingParam, {
            mode: 'cors',
            method: 'GET',
          })
            .then(function (res) {
              clearShareRef(self.state.productId);
              var purchaseShipping = (Number(self.state.price) * (self.state.selectedQuantity || 1)) >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
              var TXFEE = constants.TRANSACTION_FEE || 0.50;
              var purchaseTrackingFee = self.state.trackingUpgrade && (Number(self.state.price) * (self.state.selectedQuantity || 1)) < TRACKING_REQUIRED_THRESHOLD ? TRACKING_UPGRADE_FEE : 0;
              var purchaseTax = Math.round(Number(self.state.price) * (self.state.taxRate || 0) * 100) / 100;
              var purchaseTotal = Number(self.state.price) + purchaseShipping + purchaseTrackingFee + TXFEE + purchaseTax;
              self.props.history.push('/order-confirmation', {
                title: self.state.title,
                image: self.state.images && self.state.images[0] ? self.state.images[0] : (self.state.catalogImage || null),
                price: self.state.price,
                shippingCost: purchaseShipping,
                trackingUpgradeFee: purchaseTrackingFee,
                transactionFee: TXFEE,
                taxAmount: purchaseTax,
                grandTotal: purchaseTotal.toFixed(2),
                productId: self.state.productId,
                chargeId: res && res.chargeId ? res.chargeId : null,
                transactionId: res && res.transactionId ? res.transactionId : null,
              });
            })
            .catch(function (err) { console.log(err); });
        }
      }
    );
  }

  toCheckout() {
    if (this.state.sold) {
      this.setState({ buyError: 'Item was already sold.' });
      return;
    }
    var trackingQuery = this.state.trackingUpgrade ? '?tracking=1' : '';
    if (this.state.shareRef) {
      this.props.history.push('/metapurchase/' + this.state.shareRef + trackingQuery);
    } else {
      this.props.history.push('/purchase/' + this.state.productId + trackingQuery);
    }
  }

  handleAddToCart(cartContext) {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('signup');
      return;
    }
    var s = this.state;
    var cartShipping = (Number(s.price) * (s.selectedQuantity || 1)) >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE;
    var item = {
      postId: s.productId,
      title: s.title,
      price: s.price,
      shippingCost: cartShipping,
      image: s.images && s.images.length > 0 ? s.images[0] : '',
      cardImage: s.catalogImage || '',
      sellerId: s.seller._id,
      sellerName: s.seller.userName,
      rarity: s.cardRarity || null,
      quantity: s.selectedQuantity || 1,
      availableQuantity: s.quantity || 1,
      trackingUpgrade: s.trackingUpgrade || false,
      cardId: s.linkedCardId || null,
      condition: s.condition || null,
      foil: s.foil || false,
    };
    cartContext.addToCart(item);
    window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Added to cart!', type: 'success' } }));
  }

  openShareModal() {
    if (!Auth.loggedIn()) {
      this.context.openAuthModal('login');
      return;
    }
    var self = this;
    var userId = Auth.getProfile()._id;
    var sharedBy = this.state.sharedBy || [];

    // Check payout method — redirect to settings if missing
    var proceed = function() {
      // Check if user already shared — fetch existing link without toggling
      if (sharedBy.indexOf(userId) >= 0 || self.state.alreadyShared) {
        self.setState({ showShareModal: true, shareLoading: true, shareCopied: false });
        Auth.fetch(constants.DOMAIN + '/api/v1/posts/' + self.state.productId + '/myshare', {
          mode: 'cors',
          method: 'GET',
        }).then(function(res) {
          self.setState({
            shareLink: constants.DOMAIN + '/s/' + res.share._id,
            shareLoading: false,
            alreadyShared: true,
          });
        }).catch(function() {
          self.setState({
            shareLink: window.location.origin + '/product/' + self.state.productId,
            shareLoading: false,
          });
        });
        return;
      }

      // User hasn't shared yet — create new share
      self.setState({ showShareModal: true, shareLoading: true, shareCopied: false });
      Auth.fetch(constants.DOMAIN + '/api/v1/posts/' + self.state.productId + '/share', {
        mode: 'cors',
        method: 'POST',
      }).then(function(res) {
        var shareId = res.share && res.share._id ? res.share._id : '';
        self.setState({
          shareLink: constants.DOMAIN + '/s/' + shareId,
          shareLoading: false,
          alreadyShared: true,
          shareCount: self.state.shareCount + 1,
        });
      }).catch(function() {
        self.setState({
          shareLink: window.location.origin + '/product/' + self.state.productId,
          shareLoading: false,
        });
      });
    };

    // Show subtle reminder if no payout method, but always let them share
    if (!this.state.sharerPayoutChecked) {
      Auth.fetch(constants.DOMAIN + '/api/v1/users/' + userId, { mode: 'cors', method: 'GET' })
        .then(function(res) {
          var hasPayout = !!(res.profile && (res.profile.stripeAccountId || res.profile.paypalEmail));
          self.setState({ sharerPayoutChecked: true });
          if (!hasPayout) {
            window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Connect a payout method in Settings to collect your commissions', type: 'info' } }));
          }
          proceed();
        })
        .catch(function() { proceed(); });
    } else {
      proceed();
    }
  }

  copyShareLink() {
    var link = this.state.shareLink;
    if (!link) return;
    var self = this;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(link).then(function() {
        self.setState({ shareCopied: true });
        setTimeout(function() { self.setState({ shareCopied: false }); }, 2000);
      });
    } else {
      var el = document.createElement('textarea');
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      self.setState({ shareCopied: true });
      setTimeout(function() { self.setState({ shareCopied: false }); }, 2000);
    }
  }

  render() {
    const {
      title, images, selectedImageIndex, description, price, category,
      shippingCost, sold, quantity, selectedQuantity, seller, isFollowingSeller, loading,
      isOwner, stripeCustomerId, buyError, productId,
    } = this.state;

    const fromCardId = this.props.location.state && this.props.location.state.fromCardId;

    const api = `${constants.DOMAIN}/`;
    const isLoggedIn = Auth.loggedIn();
    const catParts = category ? category.split(' ') : [];
    const isSocial = catParts.length > 1 && catParts[1] === 'social';
    const categoryName = catParts.length > 0 ? catParts.filter(p => p !== 'social').join(' ') : '';
    const gameTag = catParts[0] || '';
    const selectedImg = images.length > 0 ? images[selectedImageIndex] : null;
    const mainImage = selectedImg
      ? (selectedImg.startsWith('http') ? selectedImg : `${api}${selectedImg}`)
      : null;

    const followHoverCSS = `
      .follow-toggle-text { display: inline; }
      .follow-toggle-text::after { content: none; }
      button:hover .follow-toggle-text { font-size: 0; }
      button:hover .follow-toggle-text::after { font-size: 0.75rem; content: 'Unfollow'; }
    `;

    if (loading) {
      return (
        <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-8">
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-8">
            {/* Image skeleton */}
            <div className="tw-w-full md:tw-w-[45%]">
              <div className="tw-aspect-square tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-xl tw-animate-pulse" />
              <div className="tw-flex tw-gap-2 tw-mt-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="tw-w-16 tw-h-16 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded-lg tw-animate-pulse" />
                ))}
              </div>
            </div>
            {/* Details skeleton */}
            <div className="tw-flex-1">
              <div className="tw-h-6 tw-w-48 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-4 tw-animate-pulse" />
              <div className="tw-h-8 tw-w-64 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-4 tw-animate-pulse" />
              <div className="tw-h-4 tw-w-32 tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-mb-6 tw-animate-pulse" />
              <div className="tw-h-10 tw-w-full tw-bg-[#e0e0e0] dark:tw-bg-[#252525] tw-rounded tw-animate-pulse" />
            </div>
          </div>
        </div>
      );
    }

    if (this.state.notFound) {
      return (
        <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-16 tw-text-center">
          <Helmet><title>Listing Not Found | Primal Marketplace</title></Helmet>
          <div className="tw-text-6xl tw-mb-4" style={{ opacity: 0.3 }}>&#128722;</div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-800 dark:tw-text-gray-200 tw-mb-2">Listing Not Found</h1>
          <p className="tw-text-gray-500 dark:tw-text-gray-400 tw-mb-6">This listing may have been removed or is no longer available.</p>
          <Link to="/browse" className="tw-inline-block tw-px-6 tw-py-2.5 tw-rounded-lg tw-font-semibold tw-text-white" style={{ backgroundColor: '#b8860b' }}>Browse Cards</Link>
        </div>
      );
    }

    return (
      <div className="tw-max-w-6xl tw-mx-auto tw-px-4 tw-py-6 md:tw-py-10 tw-overflow-x-hidden">
        <style>{followHoverCSS}</style>
        <Helmet>
          <title>{title} | Primal Marketplace</title>
          <meta name="description" content={description} />
          <meta property="og:title" content={`${title} | Primal Marketplace`} />
          <meta property="og:description" content={description} />
          <meta property="og:type" content="product" />
          {mainImage && <meta property="og:image" content={mainImage} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={`${title} | Primal Marketplace`} />
          <meta name="twitter:description" content={description} />
          {mainImage && <meta name="twitter:image" content={mainImage} />}
          {title && price > 0 && (
            <script type="application/ld+json">{JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": title,
              "description": description,
              "image": mainImage || undefined,
              "offers": {
                "@type": "Offer",
                "price": price.toFixed(2),
                "priceCurrency": "USD",
                "availability": sold ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
                "seller": seller.userName ? { "@type": "Person", "name": seller.userName } : undefined
              }
            })}</script>
          )}
        </Helmet>

        {/* Back to card page — only when navigated from a card page */}
        {fromCardId && (
          <Link
            to={`/card/${fromCardId}`}
            className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 hover:tw-text-brand-gold tw-no-underline tw-transition-colors tw-mb-2 tw-inline-block"
          >
            &larr; Back to card page
          </Link>
        )}

        {/* Breadcrumb */}
        <nav className="tw-mb-6 tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-pl-0 tw-overflow-hidden tw-w-full">
          <Link to="/" className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors">
            Home
          </Link>
          {seller.userName && (
            <>
              <span className="tw-flex-shrink-0">/</span>
              <Link
                to={'/shop/' + seller.userName}
                className="tw-flex-shrink-0 hover:tw-text-brand-gold tw-no-underline tw-text-[#999] dark:tw-text-gray-500 tw-transition-colors"
              >
                {seller.userName}
              </Link>
            </>
          )}
          <span className="tw-flex-shrink-0">/</span>
          <span className="tw-text-[#1a1a1a] dark:tw-text-white tw-truncate tw-min-w-0">{title}</span>
        </nav>

        {/* Horizontal card: full-width blurred backdrop hero — always visible for horizontal cards */}
        {this.state.cardHorizontal && mainImage && (
          <div className="tw-relative tw-rounded-2xl tw-overflow-hidden tw-mb-4" style={{ minHeight: '280px' }}>
            {sold && (
              <div className="tw-absolute tw-top-3 tw-left-3 tw-z-10 tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold tw-uppercase tw-px-3 tw-py-1 tw-rounded">
                Sold
              </div>
            )}
            {/* Blurred background */}
            <img
              src={mainImage}
              alt=""
              crossOrigin="anonymous"
              className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
              style={{ filter: 'blur(40px) saturate(1.2)', transform: 'scale(1.2)', opacity: 0.35 }}
            />
            {/* Dark overlay */}
            <div className="tw-absolute tw-inset-0" style={{ backgroundColor: 'rgba(0,0,0,0.25)' }} />
            {/* Sharp card image — rotated catalog or straight condition photo, fades on switch */}
            <div key={mainImage} className="tw-relative tw-flex tw-items-center tw-justify-center tw-py-8 tw-px-4" style={{ animation: 'pvFadeIn 0.3s ease' }}>
              {this.state.catalogImage && mainImage === this.state.catalogImage ? (
                <div className="tw-relative tw-overflow-hidden tw-rounded-lg" style={{ aspectRatio: '3/2', maxWidth: '600px', width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}>
                  <img
                    src={mainImage}
                    alt={title}
                    crossOrigin="anonymous"
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      height: '150%',
                      transform: 'translate(-50%, -50%) rotate(' + (this.state.cardRotation || -90) + 'deg)',
                    }}
                  />
                </div>
              ) : (
                <img
                  src={mainImage}
                  alt={title}
                  crossOrigin="anonymous"
                  className="tw-rounded-lg tw-max-h-[420px] tw-max-w-full tw-object-contain tw-relative"
                  style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}
                />
              )}
            </div>
            {this.state.catalogImage && mainImage === this.state.catalogImage && (
              <span className="tw-absolute tw-bottom-2 tw-right-2 tw-bg-black/50 tw-text-white tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded">
                Catalog Image
              </span>
            )}

            {/* Condition photos + catalog thumb strip inside hero */}
            {((this.state.conditionPhotos && this.state.conditionPhotos.length > 0) || this.state.catalogImage) && (
              <div className="tw-relative tw-px-4 tw-pb-3">
                <p className="tw-text-[10px] tw-font-semibold tw-text-white/70 tw-uppercase tw-tracking-wide tw-mb-1.5">Photos</p>
                <div className="tw-flex tw-gap-2">
                  {this.state.conditionPhotos.map((img, idx) => {
                    var thumbSrc = img.startsWith('http') ? img : (api + img);
                    return (
                      <button
                        key={idx}
                        onClick={() => this.setState({ images: [img], selectedImageIndex: 0 })}
                        className={'tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-bg-black/30 tw-p-0 tw-transition-all' + (mainImage === img ? ' tw-border-white' : ' tw-border-white/30 hover:tw-border-white/60')}
                      >
                        <img src={thumbSrc} alt={'Condition ' + (idx + 1)} className="tw-max-w-full tw-max-h-full tw-object-contain" />
                      </button>
                    );
                  })}
                  {this.state.catalogImage && (
                    <button
                      onClick={() => this.setState({ images: [this.state.catalogImage], selectedImageIndex: 0 })}
                      className={'tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-bg-black/30 tw-p-0 tw-transition-all' + (this.state.catalogImage === mainImage ? ' tw-border-white' : ' tw-border-white/30 hover:tw-border-white/60')}
                    >
                      <img src={this.state.catalogImage} alt="Catalog" className="tw-max-w-full tw-max-h-full tw-object-contain" />
                      <span className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-text-[8px] tw-text-white tw-bg-black/60 tw-text-center">Catalog</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main two-column layout — stays side-by-side for vertical cards, stacks for horizontal (matches CardDetail) */}
        <div className={'tw-flex tw-flex-col tw-gap-6 md:tw-gap-8' + (this.state.cardHorizontal ? '' : ' md:tw-flex-row')}>

          {/* LEFT: Image Gallery — hidden for horizontal cards (hero above handles all images) */}
          <div className={'tw-flex-shrink-0' + (this.state.cardHorizontal ? ' tw-hidden' : ' tw-w-full md:tw-w-[42%]')}>
            {/* Main image — regular square container with blurred pillarbox for mismatched aspect ratios */}
            <div className="tw-relative tw-aspect-square tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center">
              {sold && (
                <div className="tw-absolute tw-top-3 tw-left-3 tw-z-20 tw-bg-red-600 tw-text-white tw-text-xs tw-font-bold tw-uppercase tw-px-3 tw-py-1 tw-rounded">
                  Sold
                </div>
              )}
              {mainImage ? (
                <>
                  {/* Blurred background fill */}
                  <img
                    src={mainImage}
                    alt=""
                    className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover"
                    style={{ filter: 'blur(40px) saturate(1.2)', transform: 'scale(1.2)', opacity: 0.35 }}
                  />
                  {/* Sharp image centered — absolute position for Safari compatibility */}
                  <img
                    key={'sq-' + mainImage}
                    src={mainImage}
                    alt={title}
                    className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-contain tw-z-10"
                    style={{ animation: 'pvFadeIn 0.3s ease' }}
                  />
                  {this.state.catalogImage && mainImage === this.state.catalogImage && (
                    <span className="tw-absolute tw-bottom-2 tw-right-2 tw-z-20 tw-bg-black/50 tw-text-white tw-text-[10px] tw-font-medium tw-px-2 tw-py-0.5 tw-rounded">
                      Catalog Image
                    </span>
                  )}
                </>
              ) : (
                <span className="tw-text-[#999] dark:tw-text-gray-600">No Image</span>
              )}
            </div>

            {/* Condition photos strip — only shows user-uploaded photos */}
            {this.state.conditionPhotos && this.state.conditionPhotos.length > 0 && (
              <div className="tw-mt-3">
                <p className="tw-text-[10px] tw-font-semibold tw-text-[#999] tw-uppercase tw-tracking-wide tw-mb-1.5">Condition Photos</p>
                <div className="tw-flex tw-gap-2">
                  {this.state.conditionPhotos.map((img, idx) => {
                    var thumbSrc = img.startsWith('http') ? img : (api + img);
                    return (
                      <button
                        key={idx}
                        onClick={() => this.setState({ images: [img], selectedImageIndex: 0 })}
                        className="tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-p-0 tw-transition-all hover:tw-border-[#999]"
                      >
                        <img
                          src={thumbSrc}
                          alt={'Condition ' + (idx + 1)}
                          className="tw-max-w-full tw-max-h-full tw-object-contain"
                        />
                      </button>
                    );
                  })}
                  {this.state.catalogImage && (
                    <button
                      onClick={() => this.setState({ images: [this.state.catalogImage], selectedImageIndex: 0 })}
                      className="tw-relative tw-w-16 tw-h-16 tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-p-0 tw-transition-all hover:tw-border-[#999]"
                    >
                      <img
                        src={this.state.catalogImage}
                        alt="Catalog"
                        className="tw-max-w-full tw-max-h-full tw-object-contain"
                      />
                      <span className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-bg-black/60 tw-text-white tw-text-[8px] tw-text-center tw-py-0.5 tw-font-medium">
                        Catalog
                      </span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Details + Buy + Seller */}
          <div className="tw-flex-1 tw-min-w-0">

            {/* Title */}
            <h1 className="tw-text-xl md:tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2 tw-leading-tight">
              {title}
            </h1>

            {/* Category / Sealed type */}
            {this.state.productType === 'sealed' ? (
              <div className="tw-mb-3">
                <span
                  className="tw-inline-block tw-text-xs tw-font-bold tw-text-white tw-px-3 tw-py-1 tw-rounded-full"
                  style={{ backgroundColor: '#7c3aed' }}
                >
                  <i className="fa fa-cube tw-mr-1" />
                  {formatSealedType(this.state.sealedType)}
                </span>
              </div>
            ) : categoryName && !isSocial ? (
              <div className="tw-mb-3">
                <span
                  className="tw-inline-block tw-text-xs tw-font-medium tw-text-brand-gold tw-bg-brand-gold/10 tw-px-3 tw-py-1 tw-rounded-full"
                >
                  {({ 'NM': 'Near Mint', 'NMF': 'Near Mint Foil', 'LP': 'Lightly Played', 'LPF': 'Lightly Played Foil', 'MP': 'Moderately Played' })[categoryName.replace(/^singles\s*/i, '')] || categoryName}
                </span>
              </div>
            ) : null}

            {/* View Card Details link (not for sealed) */}
            {this.state.linkedCardId && !fromCardId && this.state.productType !== 'sealed' && (
              <Link
                to={'/card/' + this.state.linkedCardId}
                className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-transition-colors tw-mb-3"
              >
                View Card Details &rarr;
              </Link>
            )}

            {/* Description */}
            {description && (
              <div
                className="tw-text-sm tw-text-[#555] dark:tw-text-gray-400 tw-mb-5 tw-leading-relaxed tw-whitespace-pre-wrap"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
              />
            )}

            {/* Buy Box */}
            {!isSocial && (
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-p-5 tw-mb-5">
                {/* Price */}
                <div className="tw-flex tw-items-baseline tw-gap-3 tw-mb-1">
                  <span className="tw-text-2xl tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
                    ${price ? price.toFixed(2) : '0.00'}
                  </span>
                  {(price * selectedQuantity) >= FREE_SHIPPING_THRESHOLD ? (
                    <span className="tw-text-sm tw-text-green-600 dark:tw-text-green-400 tw-font-medium">
                      Free Shipping
                    </span>
                  ) : (
                    <span className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500">
                      + ${FLAT_SHIPPING_FEE.toFixed(2)} shipping
                    </span>
                  )}
                </div>
                <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-mb-1">
                  Total: ${(function() { var TXFEE = constants.TRANSACTION_FEE || 0.50; var computedShipping = (price * selectedQuantity) >= FREE_SHIPPING_THRESHOLD ? 0 : FLAT_SHIPPING_FEE; var trackingFee = this.state.trackingUpgrade && ((price * selectedQuantity) + computedShipping) < FREE_SHIPPING_THRESHOLD ? TRACKING_UPGRADE_FEE : 0; var taxAmt = Math.round((price * selectedQuantity) * (this.state.taxRate || 0) * 100) / 100; return ((price * selectedQuantity) + computedShipping + trackingFee + TXFEE + taxAmt).toFixed(2); }.call(this))}{this.state.taxRate > 0 ? ' (incl. tax)' : ' + tax'}
                </p>
                {quantity > 1 && !sold && (
                  <p className="tw-text-xs tw-font-medium tw-text-green-600 dark:tw-text-green-400 tw-mb-1">
                    {quantity} available
                  </p>
                )}
                {!sold && !isOwner && quantity > 1 && (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                    <span className="tw-text-xs tw-text-[#999] tw-font-medium">Qty:</span>
                    <div className="tw-flex tw-items-center tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-rounded-lg tw-overflow-hidden">
                      <button
                        type="button"
                        onClick={() => this.setState(function(prev) { return { selectedQuantity: Math.max(1, prev.selectedQuantity - 1) }; })}
                        className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[#999] hover:tw-text-brand-gold tw-text-sm tw-font-bold"
                      >
                        -
                      </button>
                      <span className="tw-w-8 tw-text-center tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">
                        {selectedQuantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => this.setState(function(prev) { return { selectedQuantity: Math.min(prev.quantity, prev.selectedQuantity + 1) }; })}
                        className="tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-[#999] hover:tw-text-brand-gold tw-text-sm tw-font-bold"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
                {!sold && !isOwner && quantity <= 1 && <div className="tw-mb-3" />}

                {/* Tracking upgrade for orders under threshold */}
                {!sold && !isOwner && (price * (selectedQuantity || 1)) < TRACKING_REQUIRED_THRESHOLD && (
                  <label className="tw-flex tw-items-center tw-gap-3 tw-mb-3 tw-cursor-pointer">
                    <input
                      type="checkbox"
                      checked={this.state.trackingUpgrade}
                      onChange={() => this.setState(function(prev) { return { trackingUpgrade: !prev.trackingUpgrade }; })}
                      className="tw-w-5 tw-h-5 tw-flex-shrink-0 tw-accent-brand-gold"
                    />
                    <span className="tw-text-xs tw-text-[#1a1a1a] dark:tw-text-white tw-leading-normal">
                      Add Tracking (+${TRACKING_UPGRADE_FEE.toFixed(2)})
                    </span>
                  </label>
                )}

                {/* Buy buttons */}
                {sold || quantity <= 0 ? (
                  <div className="tw-bg-red-600/10 tw-border tw-border-red-600/30 tw-rounded-lg tw-py-3 tw-text-center">
                    <span className="tw-text-red-600 dark:tw-text-red-400 tw-font-bold tw-text-sm">
                      Sold Out
                    </span>
                  </div>
                ) : isOwner ? (
                  <div className="tw-text-center tw-py-2">
                    <p className="tw-text-sm tw-text-[#999] dark:tw-text-gray-500 tw-mb-2">This is your listing</p>
                    <Link
                      to={'/edit/' + productId}
                      className="tw-text-sm tw-font-semibold tw-no-underline tw-transition-colors"
                      style={{ color: '#c9a84c' }}
                    >
                      <i className="fa fa-pencil tw-mr-1" />Edit Listing
                    </Link>
                    {/* Share & Earn status indicator for owner */}
                    <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-[#e8e8e8] dark:tw-border-[#2a2a2a]">
                      {this.state.allowSharing ? (
                        <p className="tw-text-xs tw-text-[#888] dark:tw-text-gray-500 tw-mb-0">
                          <i className="fa fa-share-alt tw-mr-1" style={{ color: '#c9a84c' }} />
                          Share & Earn is <span className="tw-font-semibold" style={{ color: '#c9a84c' }}>enabled at ${Math.max(1, Number(this.state.shareCommission) || 1).toFixed(2)}</span>
                          {this.state.shareCount > 0 && (
                            <span> &middot; {this.state.shareCount} {this.state.shareCount === 1 ? 'person' : 'people'} promoting</span>
                          )}
                        </p>
                      ) : (
                        <p className="tw-text-xs tw-text-[#888] dark:tw-text-gray-500 tw-mb-0">
                          <i className="fa fa-share-alt tw-mr-1" />
                          Share & Earn is <span className="tw-font-semibold">off</span>
                        </p>
                      )}
                    </div>
                  </div>
                ) : !this.state.sellerShopActive ? (
                  <div className="tw-rounded-lg tw-py-3 tw-px-4 tw-text-center tw-border" style={{ backgroundColor: 'rgba(201, 168, 76, 0.08)', borderColor: 'rgba(201, 168, 76, 0.25)' }}>
                    <p className="tw-text-sm tw-font-medium tw-mb-0" style={{ color: '#a08530' }}>
                      <i className="fa fa-pause-circle tw-mr-2" />This seller's shop is currently closed
                    </p>
                  </div>
                ) : (
                  <div className="tw-flex tw-flex-col tw-gap-2">
                    <CartContext.Consumer>
                      {(cartCtx) => {
                        var self2 = this;
                        var multiQty = (selectedQuantity || 1) > 1;
                        var buyViaCart = function() {
                          if (!Auth.loggedIn()) {
                            self2.context.openAuthModal('signup');
                            return;
                          }
                          self2.handleAddToCart(cartCtx);
                          self2.props.history.push('/cart');
                        };
                        return isLoggedIn && stripeCustomerId ? (
                          <div>
                            <button
                              onClick={multiQty ? buyViaCart : self2.toPurchase}
                              className="tw-w-full tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-transition-colors tw-cursor-pointer tw-border-none"
                            >
                              {multiQty
                                ? 'Buy ' + selectedQuantity + ' · Go to Cart'
                                : (self2.state.savedCard
                                    ? '1-Click Buy · ' + self2.state.savedCard.brand + ' ····' + self2.state.savedCard.last4
                                    : '1-Click Buy')}
                            </button>
                            {!multiQty && (
                              <button
                                onClick={self2.toCheckout}
                                className="tw-w-full tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xs tw-text-[#888] hover:tw-text-[#555] dark:hover:tw-text-[#ccc] tw-mt-4 tw-py-1 tw-transition-colors"
                              >
                                Use a different payment method
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            onClick={multiQty ? buyViaCart : (isLoggedIn ? self2.toCheckout : function() { self2.props.history.push(self2.state.shareRef ? '/metapurchase/' + self2.state.shareRef : '/purchase/' + productId); })}
                            className="tw-w-full tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-2.5 tw-rounded-lg tw-text-sm tw-transition-colors tw-cursor-pointer tw-border-none"
                          >
                            {multiQty ? 'Buy ' + selectedQuantity + ' · Go to Cart' : 'Buy Now'}
                          </button>
                        );
                      }}
                    </CartContext.Consumer>


                    {/* Cart + Share side-by-side */}
                    <div className="tw-flex tw-gap-2">
                      {/* Add to Cart */}
                      <CartContext.Consumer>
                        {(cartCtx) => {
                          var inCart = cartCtx.isInCart(productId);
                          var cartItem = inCart ? cartCtx.cart.find(function(ci) { return ci.postId === productId; }) : null;
                          var cartQty = cartItem ? (cartItem.quantity || 1) : 0;
                          var availQty = quantity || 1;
                          var canAddMore = inCart && cartQty < availQty;
                          return inCart ? (
                            <div className="tw-flex-1 tw-flex tw-gap-1">
                              <Link
                                to="/cart"
                                className="tw-flex-1 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-[#999] tw-bg-transparent tw-text-center tw-no-underline tw-flex tw-items-center tw-justify-center"
                              >
                                In Cart ({cartQty})
                              </Link>
                              {canAddMore && (
                                <button
                                  onClick={() => {
                                    cartCtx.updateCartQuantity(productId, cartQty + 1, availQty);
                                    window.dispatchEvent(new CustomEvent('toast', { detail: { text: 'Added another copy!', type: 'success' } }));
                                  }}
                                  className="tw-px-3 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-border tw-border-brand-gold tw-text-brand-gold tw-bg-transparent hover:tw-bg-brand-gold/10 tw-transition-colors tw-cursor-pointer"
                                  title="Add another copy to cart"
                                >
                                  +1
                                </button>
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={() => this.handleAddToCart(cartCtx)}
                              className="tw-flex-1 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-border tw-border-brand-gold tw-text-brand-gold tw-bg-transparent hover:tw-bg-brand-gold/10 tw-transition-colors tw-cursor-pointer"
                            >
                              Add to Cart
                            </button>
                          );
                        }}
                      </CartContext.Consumer>

                      {/* Share & Earn Button — only if seller allows sharing and price >= $10 */}
                      {this.state.allowSharing && this.state.price >= SHARE_MIN_PRICE && (
                        <button
                          onClick={this.openShareModal}
                          className="tw-flex-1 tw-py-2 tw-rounded-lg tw-text-xs tw-font-bold tw-cursor-pointer tw-transition-colors tw-border-none"
                          style={{ backgroundColor: '#c9a84c', color: 'white' }}
                        >
                          {this.state.alreadyShared ? (
                            <span><i className="fa fa-check" style={{ marginRight: '4px' }} />Shared</span>
                          ) : (
                            <span><i className="fa fa-share-alt" style={{ marginRight: '4px' }} />Share & Earn</span>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Social proof */}
                    {this.state.shareCount > 0 && (
                      <span className="tw-text-xs tw-text-[#888] tw-text-center tw-block tw-mt-1">
                        {this.state.shareCount} {this.state.shareCount === 1 ? 'person is' : 'people are'} promoting this
                      </span>
                    )}
                  </div>
                )}

                {buyError && (
                  <p className="tw-text-red-500 tw-text-sm tw-mt-3 tw-mb-0 tw-font-medium">{buyError}</p>
                )}
              </div>
            )}

            {/* Social post CTA */}
            {isSocial && gameTag && (
              <div className="tw-mb-5">
                <Link
                  to={`/discover/${gameTag.toLowerCase()}`}
                  className="tw-inline-block tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-3 tw-px-8 tw-rounded-lg tw-text-sm tw-no-underline tw-transition-colors"
                >
                  Browse {gameTag}
                </Link>
              </div>
            )}

            {/* Seller Info */}
            {seller.userName && (
              <div className="tw-bg-[#f9f9f9] dark:tw-bg-[#141414] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-p-4 tw-mb-5">
                <p className="tw-text-xs tw-text-[#999] dark:tw-text-gray-500 tw-uppercase tw-font-semibold tw-tracking-wider tw-mb-3">
                  Seller
                </p>
                <div className="tw-flex tw-items-center tw-gap-3">
                  <Link to={`/shop/${seller.userName}`} className="tw-flex-shrink-0">
                    <img
                      src={seller.picture ? `${api}${seller.picture}` : ''}
                      alt={seller.userName}
                      className="tw-w-10 tw-h-10 tw-rounded-full tw-object-cover tw-border tw-border-[#e0e0e0] dark:tw-border-[#333]"
                    />
                  </Link>
                  <div className="tw-flex-1 tw-min-w-0">
                    <Link
                      to={`/shop/${seller.userName}`}
                      className="tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white hover:tw-text-brand-gold tw-no-underline tw-transition-colors"
                    >
                      {seller.userName}
                    </Link>
                    {seller.rating > 0 ? (
                    <div className="tw-mt-0.5" style={{ whiteSpace: 'nowrap', lineHeight: 1 }}>
                      <StarRatingComponent
                        name="sellerRating"
                        editing={false}
                        starCount={5}
                        starColor="#c9a84c"
                        emptyStarColor="#555"
                        value={seller.rating || 0}
                      />
                    </div>
                    ) : (
                    <p className="tw-text-xs tw-m-0 tw-mt-0.5" style={{ color: '#999' }}>No reviews yet</p>
                    )}
                  </div>
                  {!isOwner && (
                    <button
                      onClick={this.toggleFollowSeller}
                      className={`tw-flex-shrink-0 tw-flex tw-items-center tw-gap-1.5 tw-py-1.5 tw-px-3 tw-rounded-lg tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors tw-border tw-whitespace-nowrap ${
                        isFollowingSeller
                          ? 'tw-bg-brand-gold/10 tw-border-brand-gold tw-text-brand-gold hover:tw-bg-red-500/10 hover:tw-border-red-500 hover:tw-text-red-500'
                          : 'tw-bg-brand-gold tw-border-brand-gold tw-text-black hover:tw-bg-brand-gold-dark'
                      }`}
                    >
                      <i className={isFollowingSeller ? 'fa fa-check' : 'fa fa-plus'} />
                      <span className={isFollowingSeller ? 'follow-toggle-text' : ''}>
                        {isFollowingSeller ? 'Following' : 'Follow'}
                      </span>
                    </button>
                  )}
                </div>
                {!isOwner && (
                  <button
                    onClick={this.handleMessageSeller}
                    className="tw-w-full tw-mt-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-px-3 tw-rounded-lg tw-text-xs tw-font-medium tw-cursor-pointer tw-transition-colors tw-border tw-border-[#ddd] dark:tw-border-[#444] tw-bg-transparent tw-text-[#555] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold"
                  >
                    <i className="fa fa-envelope-o" />
                    Message Seller
                  </button>
                )}
                {!isOwner && !sold && this.state.acceptOffers && (
                  <button
                    onClick={this.handleMakeOffer}
                    className="tw-w-full tw-mt-2 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-px-3 tw-rounded-lg tw-text-xs tw-font-bold tw-cursor-pointer tw-transition-colors tw-border-none"
                    style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#16a34a' }}
                  >
                    <i className="fa fa-tag" />
                    Make an Offer
                  </button>
                )}
              </div>
            )}

            {/* Owner Actions */}
            <div className="tw-flex tw-items-center tw-gap-4">

              {/* Owner: Edit + Delete */}
              {isOwner && !sold && (
                <>
                  <Link
                    to={`/edit/${productId}`}
                    className="tw-text-xs tw-font-medium tw-text-[#666] dark:tw-text-gray-400 hover:tw-text-brand-gold tw-no-underline tw-transition-colors tw-flex tw-items-center tw-gap-1"
                  >
                    <i className="fa fa-pencil" /> Edit
                  </Link>
                  <button
                    onClick={this.delete}
                    className="tw-text-xs tw-font-medium tw-text-[#666] dark:tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-flex tw-items-center tw-gap-1 tw-transition-colors"
                  >
                    <i className="fa fa-trash" /> Delete
                  </button>
                </>
              )}

              {/* Non-owner: Report */}
              {isLoggedIn && !isOwner && !this.state.hasReported && (
                <button
                  onClick={this.report}
                  className="tw-text-xs tw-font-medium tw-text-[#666] dark:tw-text-gray-400 hover:tw-text-red-500 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-flex tw-items-center tw-gap-1 tw-transition-colors"
                >
                  <i className="fa fa-flag" /> Report
                </button>
              )}
              {isLoggedIn && !isOwner && this.state.hasReported && (
                <span className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-xs tw-font-medium tw-text-red-500 tw-flex tw-items-center tw-gap-1">
                    <i className="fa fa-flag" /> Reported
                  </span>
                  <button
                    onClick={this.undoReport}
                    disabled={this.state.reportSubmitting}
                    className="tw-text-xs tw-font-medium tw-text-[#999] dark:tw-text-gray-500 hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0 tw-transition-colors"
                  >
                    {this.state.reportSubmitting ? '...' : 'Undo'}
                  </button>
                </span>
              )}
            </div>

          </div>
        </div>

        {/* More from this seller */}
        {this.state.sellerPosts.length > 0 && seller.userName && (
          <div className="tw-mt-8 tw-border-t tw-border-[#e0e0e0] dark:tw-border-[#333] tw-pt-6">
            <style>{`
              .seller-scroll::-webkit-scrollbar { height: 6px; }
              .seller-scroll::-webkit-scrollbar-track { background: transparent; }
              .seller-scroll::-webkit-scrollbar-thumb { background: #d1d1d1; border-radius: 3px; }
              .seller-scroll::-webkit-scrollbar-thumb:hover { background: #bbb; }
              .dark .seller-scroll::-webkit-scrollbar-thumb { background: #333; }
              .dark .seller-scroll::-webkit-scrollbar-thumb:hover { background: #444; }
            `}</style>
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
              <h2 className="tw-text-base tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white">
                More from {seller.userName}
              </h2>
              <Link
                to={'/shop/' + seller.userName}
                className="tw-text-xs tw-font-medium tw-text-brand-gold hover:tw-text-brand-gold-dark tw-no-underline tw-transition-colors"
              >
                View all listings <i className="fa fa-arrow-right tw-ml-1"></i>
              </Link>
            </div>
            <div
              className="seller-scroll tw-flex tw-gap-4 tw-overflow-x-auto tw-pb-4 tw-snap-x tw-snap-mandatory"
              style={{ scrollbarWidth: 'thin', scrollbarColor: document.documentElement.classList.contains('tw-dark') ? '#333 transparent' : '#d1d1d1 transparent' }}
            >
              {this.state.sellerPosts.map(post => {
                var postImage;
                if (post.cardImage) {
                  postImage = resolveCardImage(post.cardImage);
                } else if (post.images && post.images.length > 0) {
                  postImage = api + post.images[0];
                } else {
                  postImage = null;
                }
                var isHoriz = post.cardHorizontal === true;
                return (
                  <Link
                    key={post._id}
                    to={'/product/' + post._id}
                    className={'tw-flex-shrink-0 tw-snap-start tw-group tw-flex tw-flex-col' + (isHoriz ? ' tw-w-64' : ' tw-w-40')}
                    style={{ textDecoration: 'none' }}
                  >
                    <div className={'tw-bg-[#f5f5f5] dark:tw-bg-[#1a1a1a] tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-overflow-hidden tw-flex tw-items-center tw-justify-center tw-mb-2 group-hover:tw-border-brand-gold tw-transition-colors tw-relative' + (isHoriz ? '' : ' tw-aspect-[3/4]')} style={isHoriz ? { aspectRatio: '14/9' } : {}}>
                      {postImage ? (
                        isHoriz ? (
                          <img
                            src={postImage}
                            alt={post.title}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              height: '150%',
                              transform: 'translate(-50%, -50%) rotate(' + (post.cardRotation || -90) + 'deg)',
                            }}
                            loading="lazy"
                          />
                        ) : (
                          <img
                            src={postImage}
                            alt={post.title}
                            className="tw-max-w-full tw-max-h-full tw-object-contain"
                            loading="lazy"
                          />
                        )
                      ) : (
                        <div className="tw-text-[#bbb] dark:tw-text-[#555]">
                          <i className="fa fa-image tw-text-3xl"></i>
                        </div>
                      )}
                      {post.price >= FREE_SHIPPING_THRESHOLD ? (
                        <div className="tw-absolute tw-top-1.5 tw-right-1.5 tw-bg-green-600/90 tw-text-white tw-text-[8px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-full">
                          Free Shipping
                        </div>
                      ) : (
                        <div className="tw-absolute tw-top-1.5 tw-right-1.5 tw-bg-[#555]/90 tw-text-white tw-text-[8px] tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded-full">
                          + ${FLAT_SHIPPING_FEE.toFixed(2)} shipping
                        </div>
                      )}
                      <div className="tw-absolute tw-bottom-1.5 tw-left-1.5 tw-bg-black/75 tw-text-brand-gold tw-text-[11px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full">
                        ${(post.price || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="tw-mt-auto tw-w-full">
                      <p className="tw-text-xs tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white group-hover:tw-text-brand-gold tw-transition-colors tw-truncate tw-mb-0.5">
                        {(post.title || '').replace(/\s*\(Foil\)/i, '')}
                      </p>
                      <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-x-1 tw-gap-y-0.5 tw-mb-0">
                        {post.category ? (
                          <span className="tw-text-[10px] tw-text-[#999] dark:tw-text-[#666]">
                            {(function() {
                              var condMap = { 'NM': 'Near Mint', 'LP': 'Lightly Played', 'MP': 'Moderately Played', 'HP': 'Heavily Played' };
                              var raw = post.category.replace(/^singles\s*/i, '');
                              var isFoil = raw.endsWith('F');
                              var label = isFoil ? raw.replace(/F$/, '') : raw;
                              return (condMap[label] || label) + (isFoil ? ' Foil' : '');
                            })()}
                          </span>
                        ) : <span />}
                        {renderRarityBadge(post.cardRarity, null)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Share & Earn Modal */}
        {this.state.showShareModal && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-40"
              style={{ backdropFilter: 'blur(4px)' }}
              onClick={() => this.setState({ showShareModal: false })}
            />
            <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4">
              <div className="modal-enter tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-w-full tw-max-w-md tw-p-6 tw-shadow-xl">
                <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                  <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0">
                    Share & Earn ${Math.max(1, Number(this.state.shareCommission) || 1).toFixed(2)}
                  </h3>
                  <button
                    onClick={() => this.setState({ showShareModal: false })}
                    className="tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xl tw-p-0 tw-leading-none"
                  >
                    &times;
                  </button>
                </div>
                <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-5">
                  Share this listing. If someone buys through your link, you earn a ${Math.max(1, Number(this.state.shareCommission) || 1).toFixed(2)} commission.
                </p>

                {/* Share Link */}
                <div className="tw-mb-4">
                  <label className="tw-text-xs tw-font-medium tw-text-[#555] dark:tw-text-[#aaa] tw-mb-2 tw-block">Your share link</label>
                  <div className="tw-flex tw-gap-2">
                    <input
                      type="text"
                      readOnly
                      value={this.state.shareLoading ? 'Generating...' : this.state.shareLink}
                      className="tw-flex-1 tw-bg-[#f5f5f5] dark:tw-bg-[#0f0f0f] tw-rounded-full tw-px-5 tw-py-3 tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-text-sm tw-text-[#111] dark:tw-text-white tw-outline-none"
                    />
                    <button
                      onClick={this.copyShareLink}
                      disabled={this.state.shareLoading}
                      className="tw-px-4 tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-medium tw-cursor-pointer tw-border-none tw-transition-colors tw-flex-shrink-0"
                      style={{ backgroundColor: this.state.shareCopied ? '#16a34a' : '#c9a84c', color: 'white' }}
                    >
                      {this.state.shareCopied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </div>

                {/* Social Share Buttons */}
                <div className="tw-flex tw-gap-3 tw-justify-center">
                  <button
                    onClick={() => {
                      var link = this.state.shareLink || ('https://marketplace.primaltcg.com/product/' + productId);
                      var text = encodeURIComponent('Check out ' + title + ' on Primal Marketplace!');
                      window.open('https://twitter.com/intent/tweet?url=' + encodeURIComponent(link) + '&text=' + text, '_blank');
                    }}
                    className="tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-border-none tw-transition-transform hover:tw-scale-110"
                    style={{ backgroundColor: '#1DA1F2', color: 'white', fontSize: '20px' }}
                    title="Share on Twitter"
                  >
                    <i className="fa fa-twitter" />
                  </button>
                  <button
                    onClick={() => {
                      var link = this.state.shareLink || ('https://marketplace.primaltcg.com/product/' + productId);
                      window.open('https://www.facebook.com/sharer/sharer.php?u=' + encodeURIComponent(link), '_blank');
                    }}
                    className="tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-border-none tw-transition-transform hover:tw-scale-110"
                    style={{ backgroundColor: '#4267B2', color: 'white', fontSize: '20px' }}
                    title="Share on Facebook"
                  >
                    <i className="fa fa-facebook" />
                  </button>
                  <button
                    onClick={() => {
                      var link = this.state.shareLink || ('https://marketplace.primaltcg.com/product/' + productId);
                      var img = images && images.length > 0 ? images[0] : '';
                      window.open('https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(link) + '&media=' + encodeURIComponent(img) + '&description=' + encodeURIComponent(title), '_blank');
                    }}
                    className="tw-w-12 tw-h-12 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-border-none tw-transition-transform hover:tw-scale-110"
                    style={{ backgroundColor: '#E60023', color: 'white', fontSize: '20px' }}
                    title="Pin on Pinterest"
                  >
                    <i className="fa fa-pinterest" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Report Modal */}
        {this.state.reportModalOpen && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-40"
              style={{ backdropFilter: 'blur(4px)' }}
              onClick={() => this.setState({ reportModalOpen: false })}
            />
            <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4">
              <div className="modal-enter tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-w-full tw-max-w-md tw-p-6 tw-shadow-xl">
                {this.state.reportSuccess || this.state.reportAlreadyReported ? (
                  <div className="tw-text-center">
                    <div className={`tw-text-3xl tw-mb-3 ${this.state.reportAlreadyReported ? 'tw-text-[#999]' : 'tw-text-green-500'}`}>
                      {this.state.reportAlreadyReported ? '\u2139' : '\u2713'}
                    </div>
                    <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">
                      {this.state.reportAlreadyReported ? 'Already Reported' : 'Report Submitted'}
                    </h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-4">
                      {this.state.reportAlreadyReported
                        ? 'You have already reported this listing. Our team is reviewing it.'
                        : 'Thank you for reporting this listing. Our team will review it.'}
                    </p>
                    <div className="tw-flex tw-gap-3 tw-justify-center">
                      <button
                        onClick={() => this.setState({ reportModalOpen: false })}
                        className="tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-2 tw-px-6 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none tw-transition-colors"
                      >
                        Close
                      </button>
                      {(this.state.reportAlreadyReported || this.state.reportSuccess) && (
                        <button
                          onClick={this.undoReport}
                          disabled={this.state.reportSubmitting}
                          className="tw-bg-transparent tw-border tw-border-[#ddd] dark:tw-border-[#333] tw-text-[#666] dark:tw-text-gray-400 hover:tw-text-red-500 hover:tw-border-red-300 tw-font-medium tw-py-2 tw-px-6 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-transition-colors"
                        >
                          {this.state.reportSubmitting ? 'Removing...' : 'Undo Report'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                      <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0">
                        Report Listing
                      </h3>
                      <button
                        onClick={() => this.setState({ reportModalOpen: false })}
                        className="tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xl tw-p-0 tw-leading-none"
                      >
                        &times;
                      </button>
                    </div>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-4">
                      Why are you reporting this listing?
                    </p>
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-mb-4">
                      {['Counterfeit or fake item', 'Misleading listing', 'Inappropriate content', 'Spam', 'Wrong category', 'Other'].map(reason => (
                        <button
                          key={reason}
                          onClick={() => this.setState({ reportReason: reason })}
                          className={`tw-text-left tw-py-2.5 tw-px-3 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-transition-colors tw-border ${
                            this.state.reportReason === reason
                              ? 'tw-border-brand-gold tw-bg-brand-gold/10 tw-text-brand-gold tw-font-medium'
                              : 'tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-text-[#1a1a1a] dark:tw-text-white hover:tw-border-brand-gold'
                          }`}
                        >
                          {reason}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={this.state.reportDetails}
                      onChange={e => this.setState({ reportDetails: e.target.value })}
                      placeholder="Additional details (optional)"
                      maxLength={500}
                      rows={3}
                      className="tw-w-full tw-px-3 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold tw-resize-none tw-mb-4"
                    />
                    <button
                      onClick={() => this.submitReport()}
                      disabled={!this.state.reportReason || this.state.reportSubmitting}
                      className={`tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-cursor-pointer tw-border-none tw-transition-colors ${
                        !this.state.reportReason || this.state.reportSubmitting
                          ? 'tw-bg-[#e0e0e0] dark:tw-bg-[#333] tw-text-[#999] tw-cursor-not-allowed'
                          : 'tw-bg-red-600 hover:tw-bg-red-700 tw-text-white'
                      }`}
                    >
                      {this.state.reportSubmitting ? 'Submitting...' : 'Submit Report'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* Message Seller Modal */}
        {this.state.messageModalOpen && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-bg-black/50 tw-z-40"
              style={{ backdropFilter: 'blur(4px)' }}
              onClick={() => this.setState({ messageModalOpen: false })}
            />
            <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-px-4">
              <div className="modal-enter tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-w-full tw-max-w-md tw-p-6 tw-shadow-xl">
                {this.state.messageSent ? (
                  <div className="tw-text-center">
                    <div className="tw-text-3xl tw-mb-3 tw-text-green-500">&#10003;</div>
                    <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-2">Message Sent</h3>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-gray-400 tw-mb-4">
                      Your message has been sent to {this.state.seller.userName || 'the seller'}.
                    </p>
                    <div className="tw-flex tw-gap-3 tw-justify-center">
                      <button
                        onClick={() => this.setState({ messageModalOpen: false })}
                        className="tw-bg-brand-gold hover:tw-bg-brand-gold-dark tw-text-black tw-font-bold tw-py-2 tw-px-6 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-border-none tw-transition-colors"
                      >
                        Close
                      </button>
                      <button
                        onClick={() => this.props.history.push('/messages')}
                        className="tw-bg-transparent tw-border tw-border-[#ddd] dark:tw-border-[#333] tw-text-[#555] dark:tw-text-gray-400 hover:tw-border-brand-gold hover:tw-text-brand-gold tw-font-medium tw-py-2 tw-px-6 tw-rounded-lg tw-text-sm tw-cursor-pointer tw-transition-colors"
                      >
                        View Messages
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                      <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-0">
                        Message Seller
                      </h3>
                      <button
                        onClick={() => this.setState({ messageModalOpen: false })}
                        className="tw-text-[#999] hover:tw-text-[#666] dark:hover:tw-text-gray-300 tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xl tw-p-0 tw-leading-none"
                      >
                        &times;
                      </button>
                    </div>
                    {/* Listing Preview */}
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4 tw-p-3 tw-rounded-lg tw-border tw-border-[#e8e8e8] dark:tw-border-[#333] tw-bg-[#f9f9f9] dark:tw-bg-[#141414]">
                      {this.state.images && this.state.images.length > 0 && (
                        <div className="tw-w-12 tw-h-12 tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0 tw-bg-[#eee]">
                          <img src={this.state.images[0]} alt="" className="tw-w-full tw-h-full tw-object-cover" />
                        </div>
                      )}
                      <div className="tw-flex-1 tw-min-w-0">
                        <p className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-truncate">{this.state.title}</p>
                        {this.state.price != null && (
                          <p className="tw-text-sm tw-font-bold tw-m-0" style={{ color: '#c9a84c' }}>${Number(this.state.price).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                    {/* Recipient */}
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                      <span className="tw-text-xs tw-text-[#999]">To:</span>
                      <div className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-[#f0f0f0] dark:tw-bg-[#333] tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
                        {this.state.seller.profilePic ? (
                          <img src={this.state.seller.profilePic} alt="" className="tw-w-full tw-h-full tw-object-cover" />
                        ) : (
                          <i className="fa fa-user tw-text-[10px] tw-text-[#999]" />
                        )}
                      </div>
                      <span className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white">{this.state.seller.userName}</span>
                    </div>
                    <textarea
                      value={this.state.messageText}
                      onChange={e => this.setState({ messageText: e.target.value })}
                      placeholder="Write your message..."
                      maxLength={2000}
                      rows={4}
                      autoFocus
                      className="tw-w-full tw-px-3 tw-py-2.5 tw-text-sm tw-rounded-lg tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold tw-resize-none tw-mb-4"
                    />
                    <button
                      onClick={this.handleSendMessage}
                      disabled={!this.state.messageText.trim() || this.state.messageSending}
                      className={`tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-cursor-pointer tw-border-none tw-transition-colors ${
                        !this.state.messageText.trim() || this.state.messageSending
                          ? 'tw-bg-[#e0e0e0] dark:tw-bg-[#333] tw-text-[#999] tw-cursor-not-allowed'
                          : 'tw-text-black hover:tw-opacity-90'
                      }`}
                      style={this.state.messageText.trim() && !this.state.messageSending ? { backgroundColor: '#c9a84c' } : {}}
                    >
                      {this.state.messageSending ? (
                        <><i className="fa fa-circle-o-notch fa-spin" /> Sending...</>
                      ) : (
                        <><i className="fa fa-paper-plane" /> Send Message</>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* Make Offer Modal */}
        {this.state.offerModalOpen && (
          <>
            <div
              className="tw-fixed tw-inset-0 tw-z-50"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
              onClick={() => this.setState({ offerModalOpen: false, offerAmount: '', offerSending: false, offerSent: false, offerError: '' })}
            />
            <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-p-4 tw-pointer-events-none">
              <div className="modal-enter tw-bg-white dark:tw-bg-[#1a1a1a] tw-rounded-xl tw-shadow-xl tw-w-full tw-max-w-md tw-p-5 tw-pointer-events-auto tw-relative">
                {this.state.offerSent ? (
                  <div className="tw-text-center tw-py-6">
                    <div className="tw-w-14 tw-h-14 tw-rounded-full tw-mx-auto tw-mb-4 tw-flex tw-items-center tw-justify-center" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
                      <i className="fa fa-check tw-text-2xl" style={{ color: '#16a34a' }} />
                    </div>
                    <p className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-mb-2">Offer Sent!</p>
                    <p className="tw-text-sm tw-text-[#666] dark:tw-text-[#999] tw-m-0 tw-mb-4">The seller has 48 hours to respond to your offer of <strong style={{ color: '#16a34a' }}>${Number(this.state.offerAmount).toFixed(2)}</strong>.</p>
                    <div className="tw-flex tw-gap-2">
                      <button
                        onClick={() => this.setState({ offerModalOpen: false, offerAmount: '', offerSent: false })}
                        className="tw-flex-1 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-cursor-pointer tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-transparent tw-text-[#666] dark:tw-text-[#999]"
                      >Close</button>
                      <Link
                        to="/messages"
                        className="tw-flex-1 tw-py-2 tw-rounded-lg tw-text-sm tw-font-bold tw-text-center tw-text-black tw-no-underline"
                        style={{ backgroundColor: '#c9a84c' }}
                      >View Messages</Link>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-4">
                      <h3 className="tw-text-lg tw-font-bold tw-text-[#1a1a1a] dark:tw-text-white tw-m-0">Make an Offer</h3>
                      <button
                        onClick={() => this.setState({ offerModalOpen: false, offerAmount: '', offerError: '' })}
                        className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-[#f0f0f0] dark:tw-bg-[#333] tw-border-none tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-text-[#666] dark:tw-text-[#999]"
                      >
                        <i className="fa fa-times" />
                      </button>
                    </div>
                    {/* Listing Preview */}
                    <div className="tw-flex tw-items-center tw-gap-3 tw-mb-4 tw-p-3 tw-rounded-lg tw-border tw-border-[#e8e8e8] dark:tw-border-[#333] tw-bg-[#f9f9f9] dark:tw-bg-[#141414]">
                      {this.state.images && this.state.images.length > 0 && (
                        <div className="tw-w-12 tw-h-12 tw-rounded-lg tw-overflow-hidden tw-flex-shrink-0 tw-bg-[#eee]">
                          <img src={this.state.images[0]} alt="" className="tw-w-full tw-h-full tw-object-cover" />
                        </div>
                      )}
                      <div className="tw-flex-1 tw-min-w-0">
                        <p className="tw-text-sm tw-font-medium tw-text-[#1a1a1a] dark:tw-text-white tw-m-0 tw-truncate">{this.state.title}</p>
                        <p className="tw-text-xs tw-text-[#999] tw-m-0">Listed price: <span className="tw-font-bold" style={{ color: '#c9a84c' }}>${Number(this.state.price).toFixed(2)}</span></p>
                      </div>
                    </div>
                    {/* Offer Amount Input */}
                    {(function () {
                      var offerMin = 0.75;
                      if (this.state.allowSharing && this.state.shareCommission) {
                        var shareFloor = Math.ceil((Math.max(1, Number(this.state.shareCommission)) + 1.00) / 0.90 * 100) / 100;
                        if (shareFloor > offerMin) offerMin = shareFloor;
                      }
                      var priceFloor = Math.ceil(Number(this.state.price) * 0.20 * 100) / 100;
                      if (priceFloor > offerMin) offerMin = priceFloor;
                      return (
                        <div>
                          <label className="tw-block tw-text-sm tw-font-semibold tw-text-[#1a1a1a] dark:tw-text-white tw-mb-1.5">Your Offer</label>
                          <div className="tw-relative tw-mb-2">
                            <span className="tw-absolute tw-left-3.5 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-lg tw-font-bold tw-leading-none tw-text-[#999]">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min={offerMin}
                              max={Number(this.state.price) - 0.01}
                              value={this.state.offerAmount}
                              onChange={e => this.setState({ offerAmount: e.target.value, offerError: '' })}
                              placeholder={offerMin.toFixed(2)}
                              autoFocus
                              className="tw-w-full tw-pl-8 tw-pr-3 tw-py-3 tw-text-lg tw-font-bold tw-rounded-full tw-border tw-border-[#e0e0e0] dark:tw-border-[#333] tw-bg-white dark:tw-bg-[#141414] tw-text-[#1a1a1a] dark:tw-text-white tw-outline-none focus:tw-border-brand-gold"
                              style={{ boxSizing: 'border-box' }}
                            />
                          </div>
                          {this.state.offerError && (
                            <p className="tw-text-xs tw-m-0 tw-mb-2" style={{ color: '#ef4444' }}>{this.state.offerError}</p>
                          )}
                          {offerMin > 0.75 && (
                            <p className="tw-text-xs tw-m-0 tw-mb-2" style={{ color: '#c9a84c' }}>Minimum offer: ${offerMin.toFixed(2)} (covers Share & Earn commission)</p>
                          )}
                          <p className="tw-text-xs tw-text-[#999] tw-m-0 tw-mb-4"><i className="fa fa-clock-o" /> Offers expire in 48 hours</p>
                        </div>
                      );
                    }).bind(this)()}
                    <button
                      onClick={this.handleSendOffer}
                      disabled={!this.state.offerAmount || this.state.offerSending}
                      className={`tw-w-full tw-py-2.5 tw-rounded-lg tw-text-sm tw-font-bold tw-cursor-pointer tw-border-none tw-transition-colors ${
                        !this.state.offerAmount || this.state.offerSending
                          ? 'tw-bg-[#e0e0e0] dark:tw-bg-[#333] tw-text-[#999] tw-cursor-not-allowed'
                          : 'tw-text-white hover:tw-opacity-90'
                      }`}
                      style={this.state.offerAmount && !this.state.offerSending ? { backgroundColor: '#16a34a' } : {}}
                    >
                      {this.state.offerSending ? (
                        <><i className="fa fa-circle-o-notch fa-spin" /> Sending Offer...</>
                      ) : (
                        <><i className="fa fa-tag" /> Send Offer</>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

export default ProductViewRedesign;
